import type { MouseEvent, ReactNode } from "react";

/** Toni disponibili per i tasti azione della card.
 *
 *  PIENI (Davide 2026-09-01: «anche quei tasti li voglio pieni di colore»).
 *  Erano outline vividi — fondo bianco, bordo saturo, testo tono-700 — con
 *  hover che riempiva; ora il colore c'e' da subito e l'hover scurisce, che e'
 *  la stessa grammatica dei badge di stato.
 *
 *  Palette **Google/Material**, livelli 600-900, gli stessi hex dei badge: la
 *  micro-etichetta e' 9px, quindi vale la soglia WCAG piena (4.5:1) e i livelli
 *  chiari non passano. Il piu' tirato e' Orange 900 (4.65). */
export type ActionButtonTone =
  | "rose"
  | "violet"
  | "purple"
  | "sky"
  | "amber"
  | "teal"
  | "slate"
  | "emerald"
  | "pink";

// NIENTE varianti dark: qui. L'<html> puo' avere la classe "dark" (toggle
// Impostazioni) ma le superfici dell'app usano token custom che restano
// chiari → le varianti dark: di Tailwind scatterebbero su card bianche.
// Light-only finche' il tema scuro non e' reale su tutta l'app.
const TONE_CLASSES: Record<ActionButtonTone, string> = {
  rose: "bg-[#d93025] border-[#d93025] text-white hover:bg-[#b31412] hover:border-[#b31412]",
  violet: "bg-[#512da8] border-[#512da8] text-white hover:bg-[#43248c] hover:border-[#43248c]",
  purple: "bg-[#8430ce] border-[#8430ce] text-white hover:bg-[#6f27ad] hover:border-[#6f27ad]",
  sky: "bg-[#1967d2] border-[#1967d2] text-white hover:bg-[#155ab6] hover:border-[#155ab6]",
  amber: "bg-[#b06000] border-[#b06000] text-white hover:bg-[#8f4e00] hover:border-[#8f4e00]",
  teal: "bg-[#00695c] border-[#00695c] text-white hover:bg-[#00564b] hover:border-[#00564b]",
  emerald: "bg-[#137333] border-[#137333] text-white hover:bg-[#0f5c29] hover:border-[#0f5c29]",
  // Pink 800: «Pagato» da segnare (Davide 2026-09-28: rosa, non nero).
  pink: "bg-[#ad1457] border-[#ad1457] text-white hover:bg-[#880e4f] hover:border-[#880e4f]",
  slate: "bg-[#202124] border-[#202124] text-white hover:bg-[#3c4043] hover:border-[#3c4043]",
};

export interface ActionButtonProps {
  /** SVG icona (stroke-only, gia' dimensionata ~15px dal caller). */
  icon: ReactNode;
  /** Micro-etichetta 9-10px sotto l'icona (breve: "Reso", "LDV", …). */
  label: string;
  tone: ActionButtonTone;
  onClick?: (e: MouseEvent) => void;
  disabled?: boolean;
  /** Mostra lo spinner al posto dell'icona e disabilita il tasto. */
  busy?: boolean;
  title?: string;
  /** Nome accessibile esplicito (aria-label): vince sulla micro-etichetta
   *  visibile. Utile quando la label breve non basta a distinguere l'azione
   *  (es. "Mail ordine <id>", "Bonifico arrivato"). Additivo al contratto. */
  ariaLabel?: string;
  /** ``tile`` (default): icona sopra e micro-etichetta, per la colonna Azioni
   *  della card. ``inline``: icona accanto all'etichetta a 13px, per la testata
   *  della pagina ordine — li' lo spazio c'e', e un'etichetta da 9px si legge
   *  male (redesign 2026-10-22). Stessi toni pieni in entrambi. */
  variant?: "tile" | "inline";
}

/** Variante orizzontale: stessa altezza dei tasti della pagina ordine (38px). */
const INLINE =
  "inline-flex items-center gap-1.5 min-h-[38px] px-3 rounded-md border-[1.5px] text-[13px] font-semibold whitespace-nowrap shrink-0 transition-[background-color,border-color,transform] duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-1 disabled:opacity-45 disabled:cursor-not-allowed";

const BASE =
  // Redesign 2026-10-27: etichetta da 10px in grassetto (era 9px) e 47px di
  // larghezza: quattro in fila stanno ancora nei 212px della colonna (206 sul
  // portatile, griglia in globals.css). Premuto, il tasto si abbassa appena.
  "inline-flex flex-col items-center justify-center gap-[3px] w-[47px] min-h-[46px] px-0.5 py-1 rounded-lg border-[1.5px] text-[10px] font-semibold leading-[1.1] shrink-0 transition-[background-color,border-color,transform] duration-150 active:scale-[0.95] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40 focus-visible:ring-offset-1 disabled:opacity-45 disabled:cursor-not-allowed " +
  // Telefono (2026-10-23): l'etichetta da 9px sotto l'icona non si legge e il
  // tasto da 46px e' sotto il minimo per un pollice. Diventa una pillola
  // orizzontale alta 44px con l'etichetta a 14px, ovunque si usi la «tile».
  "max-md:w-auto max-md:min-h-[44px] max-md:flex-row max-md:gap-2 max-md:rounded-xl max-md:px-3.5 max-md:text-[14px] max-md:font-semibold max-md:active:scale-[0.97]";

/** Spinner minimale (nessuna dipendenza): cerchio che ruota, tono ereditato. */
function Spinner() {
  return (
    <svg
      className="w-[15px] h-[15px] animate-spin fill-none stroke-current"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" strokeWidth="3" className="opacity-25" />
      <path d="M21 12a9 9 0 0 0-9-9" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** Tasto azione verticale della card ordine (icona sopra, micro-etichetta
 *  sotto): compatto, colorato per tono, con ``data-no-open`` incorporato cosi'
 *  il click non fa navigare la riga. Usato dalla colonna Azioni di OrderCard e
 *  riusabile dalle pagine dettaglio (contratto condiviso F2). */
export function ActionButton({
  icon,
  label,
  tone,
  onClick,
  disabled,
  busy,
  title,
  ariaLabel,
  variant = "tile",
}: ActionButtonProps) {
  if (variant === "inline") {
    return (
      <button
        type="button"
        data-no-open
        title={title ?? label}
        aria-label={ariaLabel}
        disabled={disabled || busy}
        onClick={onClick}
        className={[INLINE, TONE_CLASSES[tone]].join(" ")}
      >
        <span className="inline-grid place-items-center w-[15px] h-[15px]">
          {busy ? <Spinner /> : icon}
        </span>
        {label}
      </button>
    );
  }
  return (
    <button
      type="button"
      data-no-open
      title={title ?? label}
      aria-label={ariaLabel}
      disabled={disabled || busy}
      onClick={onClick}
      className={[BASE, TONE_CLASSES[tone]].join(" ")}
    >
      <span className="inline-grid place-items-center w-[15px] h-[15px]">
        {busy ? <Spinner /> : icon}
      </span>
      <span className="max-w-full truncate">{label}</span>
    </button>
  );
}

/** Classe delle icone dentro un ActionButton della card lista. */
export const ACTION_ICON_CLS =
  "w-[15px] h-[15px] fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]";
